<script setup lang="ts">
import { computed, ref } from "vue";
import Button from "./Button.vue";
import Modal from "./Modal.vue";
import { apiUrl } from "../api/client";
import { can } from "../composables/permissions";
import { locale, t } from "../i18n";

/**
 * "Exportar conversa": a PDF by day with the photos, WhatsApp's text format, or that text with the
 * files in a ZIP. Internal notes and the conversation's events go in unless unticked; removed
 * messages only for people allowed to see them. The export is recorded in the audit log.
 */
const props = defineProps<{
  open: boolean;
  slug: string;
  conversationId: string | null;
}>();
const emit = defineEmits<{ close: [] }>();

const FORMATS = ["pdf", "txt", "zip"] as const;
const format = ref<(typeof FORMATS)[number]>("pdf");
const scope = ref<"thread" | "conversation">("thread");
const notes = ref(true);
const events = ref(true);
const hidden = ref(false);
const seesHidden = computed(() => can(props.slug, "conversations.view_hidden"));

function submit() {
  if (!props.conversationId) return;
  const q = new URLSearchParams({
    format: format.value,
    scope: scope.value,
    notes: notes.value ? "1" : "0",
    events: events.value ? "1" : "0",
    hidden: hidden.value && seesHidden.value ? "1" : "0",
    locale: locale.value,
  });
  // The browser downloads it with the session; nothing to keep here.
  window.location.assign(
    apiUrl(
      `/v1/c/${props.slug}/conversations/${props.conversationId}/export?${q}`,
    ),
  );
  emit("close");
}
</script>
<template>
  <Modal :open="open" :title="t('exportchat.title')" @close="emit('close')">
    <div class="export-chat">
      <fieldset class="export-chat-group">
        <legend>{{ t("exportchat.format") }}</legend>
        <label v-for="f in FORMATS" :key="f" class="choice-card"
          ><input
            v-model="format"
            class="wa-check"
            type="radio"
            name="export-format"
            :value="f"
          /><span
            ><strong>{{ t(`exportchat.${f}`) }}</strong
            ><small class="muted">{{ t(`exportchat.${f}_hint`) }}</small></span
          ></label
        >
      </fieldset>
      <fieldset class="export-chat-group">
        <legend>{{ t("exportchat.scope") }}</legend>
        <label class="export-chat-check"
          ><input
            v-model="scope"
            class="wa-check"
            type="radio"
            name="export-scope"
            value="thread"
          /><span>{{ t("exportchat.scope_thread") }}</span></label
        >
        <label class="export-chat-check"
          ><input
            v-model="scope"
            class="wa-check"
            type="radio"
            name="export-scope"
            value="conversation"
          /><span>{{ t("exportchat.scope_conversation") }}</span></label
        >
      </fieldset>
      <fieldset class="export-chat-group">
        <label class="export-chat-check"
          ><input v-model="notes" class="wa-check" type="checkbox" /><span>{{
            t("exportchat.notes")
          }}</span></label
        >
        <label class="export-chat-check"
          ><input v-model="events" class="wa-check" type="checkbox" /><span>{{
            t("exportchat.events")
          }}</span></label
        >
        <label v-if="seesHidden" class="export-chat-check"
          ><input v-model="hidden" class="wa-check" type="checkbox" /><span>{{
            t("exportchat.hidden")
          }}</span></label
        >
      </fieldset>
      <p class="small muted">{{ t("exportchat.audit") }}</p>
    </div>
    <template #footer>
      <Button variant="secondary" @click="emit('close')">{{
        t("common.cancel")
      }}</Button>
      <Button icon="download" :disabled="!conversationId" @click="submit">{{
        t("exportchat.submit")
      }}</Button>
    </template>
  </Modal>
</template>
